import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getDriverBooking, getDriverBookings, sendDriverMessage } from "../../lib/driverApi";
import type { DriverBooking } from "../../lib/driverApi";
import { useDriver } from "../../lib/driverContext";
import { toast } from "../../lib/toast";
import { MessageBubble, MessageInput, PageTitle } from "../../components/company/ui";
import { cn, shortDate, shortDateTime } from "../../lib/format";
import { useT } from "../../i18n";

/** Conversation list + thread (one per booking); on mobile only one of the two is shown. */
export default function DriverMessages() {
  const t = useT();
  const copy = t.driverPages.messages;
  const bp = t.driverPages.booking;
  const { id } = useParams();
  const { refreshSummary } = useDriver();
  const [threads, setThreads] = useState<DriverBooking[] | null>(null);
  const [selected, setSelected] = useState<DriverBooking | null>(null);
  const [text, setText] = useState("");

  const loadThreads = useCallback(() => getDriverBookings({ messages: true }).then(setThreads, () => setThreads([])), []);

  // List + open thread, refreshed every 20 s for new replies
  useEffect(() => {
    const load = () => {
      void loadThreads();
      if (id)
        getDriverBooking(id).then((b) => {
          setSelected(b);
          refreshSummary();
        }, () => setSelected(null));
    };
    load();
    const timer = setInterval(load, 20_000);
    return () => clearInterval(timer);
  }, [id, loadThreads, refreshSummary]);

  const current = id && selected?.ref === id ? selected : null;

  const send = async () => {
    const message = text.trim();
    if (!message || !current) return;
    setText("");
    try {
      setSelected(await sendDriverMessage(current.ref, message));
      void loadThreads();
    } catch (err) {
      toast.error(err);
      setText(message);
    }
  };

  return (
    <>
      <PageTitle>{copy.title}</PageTitle>
      <div className="grid min-h-105 overflow-hidden rounded-card bg-white lg:grid-cols-[300px_minmax(0,1fr)]">
        <div className={cn("border-line lg:block lg:border-r", id && "hidden")}>
          {threads?.map((th) => (
            <Link
              key={th.id}
              to={`/driver/messages/${th.ref}`}
              className={cn(
                "block border-b border-[#F0F0F0] px-4 py-3.5 text-ink no-underline",
                id === th.ref ? "bg-surface" : "bg-white hover:bg-[#FAFAFA]",
              )}
            >
              <div className="flex justify-between gap-2">
                <b className="flex items-center gap-1.5 text-sm font-medium">
                  {th.company?.name}
                  {th.unread > 0 && id !== th.ref && (
                    <span className="inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-brand px-1 text-[10px] text-white">
                      {th.unread}
                    </span>
                  )}
                </b>
                {th.lastMessage && <span className="text-[11px] text-faint">{shortDate(th.lastMessage.at)}</span>}
              </div>
              <div className="mt-0.5 text-xs text-muted">
                {th.listing ? `${th.listing.brand} ${th.listing.model} · ` : ""}
                {th.ref}
              </div>
              {th.lastMessage && <div className="mt-1 truncate text-[13px] text-ink-soft">{th.lastMessage.text}</div>}
            </Link>
          ))}
          {threads?.length === 0 && <p className="m-0 p-6 text-sm text-faint">{copy.empty}</p>}
          {!threads && <div className="m-4 h-20 animate-pulse rounded bg-surface" />}
        </div>

        {current ? (
          <div className="flex flex-col">
            <div className="flex items-center gap-2.5 border-b border-line px-4.5 py-3.5">
              <Link to="/driver/messages" className="text-brand no-underline lg:hidden" aria-label={t.common.back}>
                ←
              </Link>
              <b className="font-medium">{current.company?.name}</b>
              <Link to={`/driver/bookings/${current.ref}`} className="ml-auto text-[13px] text-brand hover:text-brand-link">
                {current.ref}
              </Link>
            </div>
            <div className="grid flex-1 content-start gap-2 px-4.5 py-4">
              {(current.messages ?? []).map((m) => (
                <MessageBubble
                  key={m.id}
                  me="driver"
                  from={m.from}
                  text={m.text}
                  meta={`${m.from === "driver" ? bp.you : m.from === "company" ? (current.company?.name ?? "") : bp.platform} · ${shortDateTime(m.at)}`}
                />
              ))}
            </div>
            {!["declined", "cancelled", "expired"].includes(current.status) && (
              <div className="border-t border-line px-4.5 py-3">
                <MessageInput value={text} onChange={setText} placeholder={copy.placeholder} onSend={() => void send()} />
              </div>
            )}
          </div>
        ) : (
          <div className={cn("items-center justify-center text-sm text-faint lg:flex", id ? "flex" : "hidden")}>
            {id ? t.common.loading : copy.select}
          </div>
        )}
      </div>
    </>
  );
}
